import React, { useEffect, useState, useCallback } from 'react';
import useUIStore from '../../store/uiStore';
import * as cashflowService from '../../services/cashflowService';
import * as projectService from '../../services/projectService';
import CashFlowTabs from '../../components/CashFlowTabs';
import ScrollX from '../../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const CONFIDENCE_COLORS = {
  confirmed: 'bg-green-100 text-green-800',
  likely: 'bg-blue-100 text-blue-800',
  tentative: 'bg-amber-100 text-amber-800',
};
const STATUS_COLORS = {
  expected: 'bg-gray-200 text-gray-800',
  received: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const initialForm = { project_id: '', stage_name: '', is_advance: false, expected_amount: '', expected_date: '', confidence: 'likely', notes: '' };

const ReceivablesPage = () => {
  const showToast = useUIStore((s) => s.showToast);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ project_id: '', status: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || `#${id}`;
  const isOverdue = (r) => r.status === 'expected' && r.expected_date < new Date().toISOString().slice(0, 10);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.status) params.status = filters.status;
      const [projRes, recvRes] = await Promise.all([
        projects.length ? Promise.resolve({ data: { projects } }) : projectService.getProjects(),
        cashflowService.getReceivables(params),
      ]);
      if (!projects.length) setProjects(projRes.data.projects || []);
      setRows(recvRes.data.receivables || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load receivables', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openAddForm = () => { setForm(initialForm); setEditingId(null); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(initialForm); };

  const handleEdit = (r) => {
    setForm({
      project_id: String(r.project_id), stage_name: r.stage_name, is_advance: r.is_advance,
      expected_amount: String(r.expected_amount), expected_date: r.expected_date,
      confidence: r.confidence, notes: r.notes || '',
    });
    setEditingId(r.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.stage_name || !form.expected_amount || !form.expected_date || (!editingId && !form.project_id)) {
      showToast('Project, stage, amount, and expected date are required', 'error');
      return;
    }
    try {
      if (editingId) {
        // project_id isn't editable once raised, same convention as material requirements.
        await cashflowService.updateReceivable(editingId, {
          stage_name: form.stage_name, is_advance: form.is_advance, expected_amount: Number(form.expected_amount),
          expected_date: form.expected_date, confidence: form.confidence, notes: form.notes || null,
        });
        showToast('Receivable updated', 'success');
      } else {
        await cashflowService.createReceivable({
          project_id: Number(form.project_id), stage_name: form.stage_name, is_advance: form.is_advance,
          expected_amount: Number(form.expected_amount), expected_date: form.expected_date,
          confidence: form.confidence, notes: form.notes || null,
        });
        showToast('Receivable added', 'success');
      }
      closeForm();
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to save receivable', 'error');
    }
  };

  const handleMarkReceived = async (r) => {
    if (!window.confirm(`Mark "${r.stage_name}" as received (${money(r.expected_amount)})?`)) return;
    try {
      await cashflowService.markReceivableReceived(r.id);
      showToast('Marked received', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update', 'error');
    }
  };

  const handleCancel = async (r) => {
    if (!window.confirm('Cancel this receivable?')) return;
    try {
      await cashflowService.cancelReceivable(r.id);
      showToast('Receivable cancelled', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to cancel', 'error');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm('Delete this receivable? This cannot be undone.')) return;
    try {
      await cashflowService.deleteReceivable(r.id);
      showToast('Receivable deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <CashFlowTabs />
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">Expected Receivables</h1>
          <button onClick={() => (showForm ? closeForm() : openAddForm())} className="text-sm px-4 py-2 rounded bg-white text-komal-blue font-semibold hover:bg-white/90">
            {showForm ? 'Cancel' : '+ Add Receivable'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="glass-card p-5 mb-6">
            {editingId && (
              <p className="text-xs text-gray-500 mb-3">
                Editing receivable #{editingId}. Use this to push out an overdue expected date to a new one,
                or to correct the amount/stage - only receivables still marked "expected" can be edited.
              </p>
            )}
            <div className="grid md:grid-cols-3 gap-3">
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                      disabled={!!editingId} className="border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-400">
                <option value="">Select project *</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input placeholder="Stage name (e.g. Advance, Milestone 2) *" value={form.stage_name} onChange={(e) => setForm({ ...form, stage_name: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <select value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm">
                <option value="confirmed">Confirmed</option>
                <option value="likely">Likely</option>
                <option value="tentative">Tentative</option>
              </select>
              <input type="number" placeholder="Expected amount (₹) *" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.is_advance} onChange={(e) => setForm({ ...form, is_advance: e.target.checked })} />
                This is an advance payment
              </label>
              <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm md:col-span-2" />
              <button type="submit" className="text-sm px-4 py-1.5 rounded bg-komal-blue text-white font-semibold">{editingId ? 'Update' : 'Save'}</button>
            </div>
          </form>
        )}

        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2 items-center">
          <select value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All statuses</option>
            <option value="expected">Expected</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Receivables ({rows.length})</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              An expected receivable whose date has passed still shows in the forecast (marked "overdue") rather
              than disappearing - edit it to push the expected date out once the client gives a new payment date.
            </p>
          </div>
          {isLoading ? (
            <p className="text-sm text-gray-400 p-5">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No receivables recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Project</th>
                    <th className="px-4 py-2 font-medium">Stage</th>
                    <th className="px-4 py-2 font-medium">Expected date</th>
                    <th className="px-4 py-2 font-medium">Confidence</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Amount</th>
                    <th className="px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">{projectName(r.project_id)}</td>
                      <td className="px-4 py-3">
                        {r.stage_name}
                        {r.is_advance && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-semibold">Credit - Advance</span>}
                      </td>
                      <td className={`px-4 py-3 ${isOverdue(r) ? 'text-red-600 font-semibold' : ''}`}>{r.expected_date}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-semibold ${CONFIDENCE_COLORS[r.confidence]}`}>{r.confidence}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-semibold ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                        {isOverdue(r) && (
                          <span className="ml-1 text-xs px-2 py-1 rounded font-semibold bg-red-100 text-red-800">overdue</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{money(r.expected_amount)}</td>
                      <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                        {r.status === 'expected' && (
                          <>
                            <button onClick={() => handleMarkReceived(r)} className="text-green-700 hover:underline text-xs font-semibold">Mark Received</button>
                            <button onClick={() => handleEdit(r)} className="text-blue-700 hover:underline text-xs font-semibold">Edit</button>
                            <button onClick={() => handleCancel(r)} className="text-gray-500 hover:underline text-xs font-semibold">Cancel</button>
                            <button onClick={() => handleDelete(r)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceivablesPage;
